import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { supabase } from '../../lib/supabase';
import PrivateImage from '../../components/common/PrivateImage';
import {
  ArrowLeft,
  CheckCircle,
  Save,
  UserCircle,
  Trophy,
  AlertCircle,
  Target,
  Clock,
  Star
} from 'lucide-react';

const MatchStats = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { userProfile } = useAuth();
  const [match, setMatch] = useState(null);
  const [calledPlayers, setCalledPlayers] = useState([]);
  const [participation, setParticipation] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    fetchData();
  }, [id]);

  const fetchData = async () => {
    try {
      setLoading(true);

      // Cargar partido
      const { data: matchData, error: matchError } = await supabase
        .from('matches')
        .select('*')
        .eq('id', id)
        .single();

      if (matchError) throw matchError;

      if (!matchData) {
        navigate('/matches');
        return;
      }

      setMatch(matchData);

      // Cargar jugadores convocados
      const { data: callupData, error: callupError } = await supabase
        .from('match_callup')
        .select(`
          *,
          players (*)
        `)
        .eq('match_id', id)
        .eq('is_called', true);

      if (callupError) throw callupError;

      const players = (callupData || []).map(c => c.players).filter(p => p);
      setCalledPlayers(players);

      // Cargar participación existente
      const { data: participationData, error: participationError } = await supabase
        .from('match_participation')
        .select('*')
        .eq('match_id', id);

      if (participationError) throw participationError;

      const participationMap = {};
      (participationData || []).forEach(part => {
        participationMap[part.player_id] = {
          minutes_played: part.minutes_played || 0,
          goals: part.goals || 0,
          assists: part.assists || 0,
          yellow_cards: part.yellow_cards || 0,
          red_cards: part.red_cards || 0,
          is_mvp: part.is_mvp || false,
          position_played: part.position_played || '',
          performance_rating: part.performance_rating || 0,
          individual_notes: part.individual_notes || '',
          id: part.id
        };
      });

      setParticipation(participationMap);

    } catch (error) {
      console.error('Error al cargar datos:', error);
      setError('Error al cargar los datos');
    } finally {
      setLoading(false);
    }
  };

  const handleParticipationChange = (playerId, field, value) => {
    setParticipation(prev => ({
      ...prev,
      [playerId]: {
        ...prev[playerId],
        [field]: value
      }
    }));
  };

  const handleMVPToggle = (playerId) => {
    setParticipation(prev => {
      const newParticipation = {};
      // Desmarcar todos los MVP
      Object.keys(prev).forEach(id => {
        newParticipation[id] = {
          ...prev[id],
          is_mvp: false
        };
      });
      // Marcar el nuevo MVP
      newParticipation[playerId] = {
        ...newParticipation[playerId],
        is_mvp: !prev[playerId]?.is_mvp
      };
      return newParticipation;
    });
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');

      const participationRecords = Object.entries(participation)
        .filter(([_, data]) => data.minutes_played > 0 || data.goals > 0 || data.assists > 0)
        .map(([playerId, data]) => ({
          match_id: parseInt(id),
          player_id: parseInt(playerId),
          minutes_played: parseInt(data.minutes_played) || 0,
          goals: parseInt(data.goals) || 0,
          assists: parseInt(data.assists) || 0,
          yellow_cards: parseInt(data.yellow_cards) || 0,
          red_cards: parseInt(data.red_cards) || 0,
          is_mvp: data.is_mvp || false,
          position_played: data.position_played || null,
          performance_rating: parseInt(data.performance_rating) || null,
          individual_notes: data.individual_notes || null
        }));

      // Eliminar participación anterior
      await supabase
        .from('match_participation')
        .delete()
        .eq('match_id', id);

      // Insertar nueva participación
      if (participationRecords.length > 0) {
        const { error: insertError } = await supabase
          .from('match_participation')
          .insert(participationRecords);

        if (insertError) throw insertError;
      }

      setSuccess(true);

      setTimeout(() => {
        navigate('/matches');
      }, 2000);

    } catch (err) {
      console.error('Error al guardar estadísticas:', err);
      setError(err.message || 'Error al guardar las estadísticas');
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('es-ES', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const formatTime = (timeString) => {
    if (!timeString) return '';
    return timeString.substring(0, 5);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Cargando...</p>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-10 h-10 text-green-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">¡Estadísticas guardadas!</h2>
          <p className="text-gray-600 mb-4">Las estadísticas se han registrado correctamente</p>
          <button
            onClick={() => navigate('/matches')}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Volver a Partidos
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/matches')}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-5 h-5 text-gray-600" />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Estadísticas del Partido</h1>
                {match && (
                  <p className="text-sm text-gray-600">
                    {match.team} vs {match.opponent} - {formatDate(match.date)}
                  </p>
                )}
              </div>
            </div>

            <button
              onClick={handleSave}
              disabled={saving}
              className="flex items-center gap-2 px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Save className="w-5 h-5" />
              {saving ? 'Guardando...' : 'Guardar Estadísticas'}
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-800">{error}</p>
          </div>
        )}

        {calledPlayers.length === 0 ? (
          <div className="bg-white rounded-xl shadow-sm p-12 text-center">
            <UserCircle className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No hay jugadores convocados</h3>
            <p className="text-gray-600 mb-6">Primero debes crear la convocatoria para este partido</p>
            <button
              onClick={() => navigate(`/matches/${id}/callup`)}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Ir a Convocatoria
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            {calledPlayers.map((player) => {
              const playerParticipation = participation[player.id] || {};

              return (
                <div
                  key={player.id}
                  className="bg-white rounded-xl shadow-sm p-6"
                >
                  <div className="flex items-start gap-6">
                    {/* Foto del jugador */}
                    <div className="flex-shrink-0">
                      {player.photo_url ? (
                        <PrivateImage
                          bucket="player-photos"
                          path={player.photo_url}
                          alt={`${player.first_name} ${player.last_name}`}
                          className="w-16 h-16 rounded-full object-cover"
                          fallbackIcon={UserCircle}
                        />
                      ) : (
                        <div className="w-16 h-16 rounded-full bg-gray-200 flex items-center justify-center">
                          <UserCircle className="w-12 h-12 text-gray-400" />
                        </div>
                      )}
                      {player.player_number && (
                        <div className="mt-1 text-center">
                          <span className="inline-block px-2 py-0.5 bg-blue-600 text-white text-xs font-bold rounded">
                            #{player.player_number}
                          </span>
                        </div>
                      )}
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between mb-4">
                        <div>
                          <h3 className="text-lg font-bold text-gray-900 mb-1">
                            {player.first_name} {player.last_name}
                          </h3>
                          {player.position && (
                            <p className="text-sm text-gray-600">{player.position}</p>
                          )}
                        </div>

                        {/* Botón MVP */}
                        <button
                          onClick={() => handleMVPToggle(player.id)}
                          className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${
                            playerParticipation.is_mvp
                              ? 'bg-yellow-500 text-white shadow-md'
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          }`}
                        >
                          <Star className="w-4 h-4" />
                          MVP
                        </button>
                      </div>

                      {/* Estadísticas del partido */}
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-4">
                        <h4 className="text-sm font-semibold text-blue-900">
                          📊 Estadísticas del Partido
                        </h4>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          {/* Minutos jugados */}
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                              <Clock className="w-3 h-3 inline mr-1" />
                              Minutos jugados
                            </label>
                            <input
                              type="number"
                              min="0"
                              max="120"
                              placeholder="0"
                              value={playerParticipation.minutes_played || ''}
                              onChange={(e) => handleParticipationChange(player.id, 'minutes_played', e.target.value)}
                              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            />
                          </div>

                          {/* Goles */}
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                              ⚽ Goles
                            </label>
                            <input
                              type="number"
                              min="0"
                              placeholder="0"
                              value={playerParticipation.goals || ''}
                              onChange={(e) => handleParticipationChange(player.id, 'goals', e.target.value)}
                              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            />
                          </div>

                          {/* Asistencias */}
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                              🎯 Asistencias
                            </label>
                            <input
                              type="number"
                              min="0"
                              placeholder="0"
                              value={playerParticipation.assists || ''}
                              onChange={(e) => handleParticipationChange(player.id, 'assists', e.target.value)}
                              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            />
                          </div>

                          {/* Tarjetas amarillas */}
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                              🟨 Tarjetas amarillas
                            </label>
                            <input
                              type="number"
                              min="0"
                              max="2"
                              placeholder="0"
                              value={playerParticipation.yellow_cards || ''}
                              onChange={(e) => handleParticipationChange(player.id, 'yellow_cards', e.target.value)}
                              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            />
                          </div>

                          {/* Tarjetas rojas */}
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                              🟥 Tarjetas rojas
                            </label>
                            <input
                              type="number"
                              min="0"
                              max="1"
                              placeholder="0"
                              value={playerParticipation.red_cards || ''}
                              onChange={(e) => handleParticipationChange(player.id, 'red_cards', e.target.value)}
                              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            />
                          </div>

                          {/* Valoración */}
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                              <Target className="w-3 h-3 inline mr-1" />
                              Valoración (1-10)
                            </label>
                            <select
                              value={playerParticipation.performance_rating || ''}
                              onChange={(e) => handleParticipationChange(player.id, 'performance_rating', e.target.value)}
                              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            >
                              <option value="">Sin valorar</option>
                              {[1,2,3,4,5,6,7,8,9,10].map(num => (
                                <option key={num} value={num}>{num}</option>
                              ))}
                            </select>
                          </div>
                        </div>

                        {/* Posición jugada */}
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            Posición jugada
                          </label>
                          <input
                            type="text"
                            placeholder="Ej: Delantero centro, Lateral derecho..."
                            value={playerParticipation.position_played || ''}
                            onChange={(e) => handleParticipationChange(player.id, 'position_played', e.target.value)}
                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          />
                        </div>

                        {/* Notas individuales */}
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            Notas individuales
                          </label>
                          <textarea
                            placeholder="Observaciones sobre el rendimiento en el partido..."
                            value={playerParticipation.individual_notes || ''}
                            onChange={(e) => handleParticipationChange(player.id, 'individual_notes', e.target.value)}
                            rows="2"
                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default MatchStats;
